<template>
  <div class="home">
    <div>
      <!-- header -->
      <section>
        <div class="py-8 px-8">
          <div class="d-flex flex-wrap align-items-center justify-content-between">
            <div class="col-12 col-lg-auto mb-3 mb-lg-0">
              <div class="d-flex align-items-center">
                <h4 class="mb-0 me-2">{{$t('employee_leaves')}}</h4>
              </div>
            </div>
            <div class="col-12 col-lg-auto d-flex align-items-center gap-2">
              <div class="input-group flex-grow-1" style="border-bottom: 1px solid #ddd;">
                <input class="form-control border-0 search-input" autocomplete="off" type="text" v-model="search" :placeholder="$t('search_employee')">
              </div>
              <!-- Mobile filter toggle -->
              <button @click="showFilters = !showFilters" class="btn btn-outline-secondary d-md-none">
                <AppIcon name="bi-funnel" class="bi bi-funnel" />
              </button>
            </div>
          </div>
        </div>
      </section>
      <!-- header -->

      <!-- Summary Cards -->
      <section class="px-8 mb-4">
        <div class="row g-2">
          <div class="col-4 col-md-4">
            <div class="card text-center">
              <div class="card-body py-2 px-2">
                <AppIcon name="bi-hourglass-split" class="bi bi-hourglass-split text-warning" style="font-size: 1.5rem" />
                <h5 class="mt-1 mb-1">{{ leaveStats.pending || 0}}</h5>
                <small class="text-muted" style="font-size: 0.75rem">{{$t('pending')}}</small>
              </div>
            </div>
          </div>
          <div class="col-4 col-md-4">
            <div class="card text-center">
              <div class="card-body py-2 px-2">
                <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill text-success" style="font-size: 1.5rem" />
                <h5 class="mt-1 mb-1">{{ leaveStats.approved || 0}}</h5>
                <small class="text-muted" style="font-size: 0.75rem">{{$t('approved')}}</small>
              </div>
            </div>
          </div>
          <div class="col-4 col-md-4">
            <div class="card text-center">
              <div class="card-body py-2 px-2">
                <AppIcon name="bi-x-circle-fill" class="bi bi-x-circle-fill text-danger" style="font-size: 1.5rem" />
                <h5 class="mt-1 mb-1">{{ leaveStats.rejected || 0}}</h5>
                <small class="text-muted" style="font-size: 0.75rem">{{$t('rejected')}}</small>
              </div>
            </div>
          </div>
        </div>
      </section>


      <section class="">
        <div class="px-8">
          <!-- Mobile action buttons -->
          <div class="d-flex d-md-none gap-2 mb-3">
            <button v-if="helper.checkAccess('create_holidays') != -1" @click="create" class="btn btn-sm btn-success flex-grow-1">
              <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
              <span>{{$t('request_leave')}}</span>
            </button>
          </div>

          <!-- Filters section (collapsible on mobile, always visible on desktop) -->
          <div class="row align-items-end g-2 mb-4" :class="{'d-none d-md-flex': !showFilters}">
            <!-- Leave Type filter -->
            <div class="col-12 col-md-3 col-lg-2">
              <label class="form-label small d-md-none mb-1">{{$t('leave_type')}}</label>
              <select v-model="filters.leave_type" class="form-select form-select-sm">
                <option value="">{{$t('all_types')}}</option>
                <option value="annual">{{$t('annual_leave')}}</option>
                <option value="sick">{{$t('sick_leave')}}</option>
                <option value="unpaid">{{$t('unpaid_leave')}}</option>
                <option value="maternity">{{$t('maternity_leave')}}</option>
                <option value="paternity">{{$t('paternity_leave')}}</option>
                <option value="compassionate">{{$t('compassionate_leave')}}</option>
              </select>
            </div>

            <!-- Status filter -->
            <div class="col-12 col-md-3 col-lg-2">
              <label class="form-label small d-md-none mb-1">{{$t('status')}}</label>
              <select v-model="filters.status" class="form-select form-select-sm">
                <option value="">{{$t('all_statuses')}}</option>
                <option value="pending">{{$t('pending')}}</option>
                <option value="approved">{{$t('approved')}}</option>
                <option value="rejected">{{$t('rejected')}}</option>
                <option value="cancelled">{{$t('cancelled')}}</option>
              </select>
            </div>

            <!-- Spacer for desktop -->
            <div class="col d-none d-md-block"></div>

            <!-- Desktop action buttons -->
            <div class="col-md-2 d-none d-md-block" v-if="helper.checkAccess('create_holidays') != -1">
              <button @click="create" class="btn btn-sm btn-success w-100">
                <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
                <span>{{$t('request_leave')}}</span>
              </button>
            </div>
          </div>

          <div class="page-table-container mb-6 bg-white rounded shadow">

            <!-- table -->
            <div class="table-responsive">
              <table class="table mb-0 table-borderless table-striped small">
                <thead>
                  <tr class="text-secondary">
                    <th class="pt-4 pb-3 px-6">{{$t('employee')}}</th>
                    <sortable-table-header field="leave_type" label="leave_type" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchLeaves)" />
                    <sortable-table-header field="start_date" label="start_date" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchLeaves)" />
                    <sortable-table-header field="end_date" label="end_date" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchLeaves)" />
                    <sortable-table-header field="days_count" label="days" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchLeaves)" />
                    <sortable-table-header field="status" label="status" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchLeaves)" />
                    <th class="pt-4 pb-3 px-6">{{$t('action')}}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="leave in leaves" :key="leave.id">
                    <td class="py-5 px-6">
                      <router-link v-if="leave.employee" :to="`/hr/employees/${leave.employee.id}`">
                        <b>{{leave.employee.name}}</b>
                      </router-link>
                      <span v-else>-</span>
                    </td>
                    <td class="py-5 px-6">{{$t(leave.leave_type)}}</td>
                    <td class="py-5 px-6">{{formatDate(leave.start_date)}}</td>
                    <td class="py-5 px-6">{{formatDate(leave.end_date)}}</td>
                    <td class="py-5 px-6"><b>{{leave.days_count}}</b></td>
                    <td class="py-5 px-6">
                      <span class="badge" :class="{
                        'bg-warning': leave.status === 'pending',
                        'bg-success': leave.status === 'approved',
                        'bg-danger': leave.status === 'rejected',
                        'bg-secondary': leave.status === 'cancelled'
                      }">
                        {{$t(leave.status)}}
                      </span>
                    </td>
                    <td class="py-5 px-6">
                      <button v-if="leave.status === 'pending' && helper.checkAccess('edit_holidays') != -1" @click="approveLeave(leave)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-success btn-xs">
                        <AppIcon name="bi-check" class="bi bi-check" style="font-size:12px" />
                        {{$t('approve')}}
                      </button>
                      <button v-if="leave.status === 'pending' && helper.checkAccess('edit_holidays') != -1" @click="rejectLeave(leave)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-danger btn-xs">
                        <AppIcon name="bi-x" class="bi bi-x" style="font-size:12px" />
                        {{$t('reject')}}
                      </button>
                      <button @click="viewLeave(leave)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary btn-xs">
                        <AppIcon name="bi-eye" class="bi bi-eye" style="font-size:12px" />
                        {{$t('view')}}
                      </button>
                      <button v-if="leave.status === 'pending' && helper.checkAccess('delete_holidays') != -1" @click="deleteLeave(leave)" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs">
                        <AppIcon name="bi-trash" class="bi bi-trash" style="font-size:12px" />
                        {{$t('delete')}}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="total > leaves.length" style="text-align:center;margin-top:1rem;margin-bottom:1rem">
              <compact-pagination :pages="pagination" :current-page="currentPage" @change="changePage" />
            </div>

            <table-loading-bar v-if="loading" class="page-table-state text-center" />
            <div v-else-if="!leaves.length && !isFiltered" class="page-table-state text-center py-5 my-5 bg-white rounded shadow-sm border" style="border-style: dashed !important;">
              <AppIcon name="bi-calendar-x" class="bi bi-calendar-x text-muted" style="font-size: 3rem;" />
              <h4 class="mt-3">{{ $t('no_leaves_added') }}</h4>
              <p class="text-muted">{{ $t('start_by_adding_leaves') }}</p>
              <template v-if="helper.checkAccess('create_holidays') != -1">
                  <button class="btn btn-outline-primary mt-2" @click="create">
                      {{ $t('create_first_leave') }}
                  </button>
              </template>
            </div>
            <div v-else-if="!leaves.length && isFiltered" class="page-table-state text-center" style="padding:1rem">{{$t('no_leave_requests')}}</div>
          </div>
          <!-- table -->

          <div class="col-sm-7">
            {{$t('show')}}
            <select v-model="perPage" class="form-select mb-3" name="Resulats" style="cursor:pointer;margin-right: 5px;width:100px;display:inline-block">
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            {{$t('entities')}}
          </div>
        </div>
      </section>
    </div>

    <!-- View Leave Modal -->
    <div class="modal fade" id="viewLeaveModal" tabindex="-1" aria-labelledby="viewLeaveModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="viewLeaveModalLabel">{{$t('leave_details')}}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-md-6">
                <p><strong>{{$t('employee')}}:</strong> {{selectedLeave.employee ? selectedLeave.employee.name : '-'}}</p>
                <p><strong>{{$t('leave_type')}}:</strong> {{$t(selectedLeave.leave_type)}}</p>
                <p><strong>{{$t('start_date')}}:</strong> {{selectedLeave.start_date ? formatDate(selectedLeave.start_date) : '-'}}</p>
                <p><strong>{{$t('end_date')}}:</strong> {{selectedLeave.end_date ? formatDate(selectedLeave.end_date) : '-'}}</p>
              </div>
              <div class="col-md-6">
                <p><strong>{{$t('days')}}:</strong> {{selectedLeave.days_count}}</p>
                <p><strong>{{$t('status')}}:</strong>
                  <span class="badge" :class="{
                    'bg-warning': selectedLeave.status === 'pending',
                    'bg-success': selectedLeave.status === 'approved',
                    'bg-danger': selectedLeave.status === 'rejected',
                    'bg-secondary': selectedLeave.status === 'cancelled'
                  }">
                    {{$t(selectedLeave.status)}}
                  </span>
                </p>
                <p v-if="selectedLeave.approved_by"><strong>{{$t('approved_by')}}:</strong> {{selectedLeave.approver ? selectedLeave.approver.name : '-'}}</p>
              </div>
            </div>
            <div v-if="selectedLeave.reason">
              <p><strong>{{$t('reason')}}:</strong></p>
              <p>{{selectedLeave.reason}}</p>
            </div>
            <div v-if="selectedLeave.rejection_reason">
              <p><strong>{{$t('rejection_reason')}}:</strong></p>
              <p>{{selectedLeave.rejection_reason}}</p>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{$t('close')}}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Leave Modal -->
    <div class="modal fade" id="createLeaveModal" tabindex="-1" aria-labelledby="createLeaveModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="createLeaveModalLabel">{{$t('request_leave')}}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">{{$t('employee')}} <span class="text-danger">*</span></label>
              <select v-model="leaveForm.employee_id" @change="onEmployeeChange" class="form-select">
                <option value="">{{$t('select_employee')}}</option>
                <option v-for="emp in employees" :key="emp.id" :value="emp.id">{{emp.name}}</option>
              </select>
            </div>

            <div class="mb-3">
              <label class="form-label">{{$t('leave_type')}} <span class="text-danger">*</span></label>
              <select v-model="leaveForm.leave_type" class="form-select">
                <option value="annual">{{$t('annual_leave')}}</option>
                <option value="sick">{{$t('sick_leave')}}</option>
                <option value="unpaid">{{$t('unpaid_leave')}}</option>
                <option value="maternity">{{$t('maternity_leave')}}</option>
                <option value="paternity">{{$t('paternity_leave')}}</option>
                <option value="compassionate">{{$t('compassionate_leave')}}</option>
              </select>
            </div>

            <div class="mb-3">
              <label class="form-label">{{$t('start_date')}} <span class="text-danger">*</span></label>
              <input type="date" v-model="leaveForm.start_date" @change="calculateDays" class="form-control">
            </div>

            <div class="mb-3">
              <label class="form-label">{{$t('end_date')}} <span class="text-danger">*</span></label>
              <input type="date" v-model="leaveForm.end_date" @change="calculateDays" class="form-control">
            </div>

            <div class="mb-3">
              <label class="form-label">{{$t('total_days')}}</label>
              <input type="number" min="0.5" step="0.5" v-model.number="leaveForm.days_count" class="form-control">
              <small class="text-muted">Jours de travail effectif: exclure le repos hebdomadaire et les jours fériés payés.</small>
            </div>

            <div class="mb-3">
              <label class="form-label">{{$t('reason')}}</label>
              <textarea v-model="leaveForm.reason" class="form-control" rows="3" :placeholder="$t('enter_reason')"></textarea>
            </div>

            <div v-if="selectedEmployee && selectedEmployee.id" class="alert alert-info small">
              <AppIcon name="bi-info-circle" class="bi bi-info-circle" />
              {{$t('annual_leave_balance')}}: <strong>{{selectedEmployee.annual_leave_balance || 0}}</strong> {{$t('days')}} |
              {{$t('sick_leave_balance')}}: <strong>{{selectedEmployee.sick_leave_balance || 0}}</strong> {{$t('days')}}
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{$t('cancel')}}</button>
            <button type="button" class="btn btn-primary" @click="saveLeave">{{$t('submit_request')}}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '@/helper'
import sortableList from '@/mixins/sortableList'

export default {
  name: 'EmployeeLeaves',
  persistedFilterState: {
    key: 'employee-leaves',
    fields: ['search', 'filters', 'perPage', 'sortField', 'sortDirection'],
  },
  mixins: [sortableList],
  data() {
    return {
      helper,
      leaves: [],
      employees: [],
      search: '',
      showFilters: false,
      filters: {
        leave_type: '',
        status: ''
      },
      currentPage: 1,
      perPage: 10,
      total: 0,
      selectedLeave: {},
      selectedEmployee: {},
      leaveForm: {
        employee_id: '',
        leave_type: 'annual',
        start_date: '',
        end_date: '',
        reason: '',
        days_count: 0
      },
      viewModal: null,
      createModal: null,
      loading: false,
      pagination: [],
      leaveStats: { pending: 0, approved: 0, rejected: 0 }
    }
  },
  watch: {
    search() {
      this.fetchLeaves()
    },
    'filters.leave_type'() {
      this.fetchLeaves()
    },
    'filters.status'() {
      this.fetchLeaves()
    },
    perPage() {
      this.fetchLeaves()
    }
  },
  computed: {
    isFiltered() {
      return (this.search && this.search.length > 0) ||
             (this.filters.leave_type && this.filters.leave_type !== '') ||
             (this.filters.status && this.filters.status !== '');
    }
  },
  mounted() {
    //check permissions
    if(helper.checkAccess('view_holidays') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_holidays') == -2) return this.$router.push({path : '/subscribe'})

    this.fetchLeaves()
    this.fetchEmployees()
    this.fetchLeaveStats()
    // eslint-disable-next-line no-undef
    this.viewModal = new bootstrap.Modal(document.getElementById('viewLeaveModal'), {backdrop: 'static', keyboard: false})
    // eslint-disable-next-line no-undef
    this.createModal = new bootstrap.Modal(document.getElementById('createLeaveModal'), {backdrop: 'static', keyboard: false})
  },
  methods: {
    formatDate(date) {
      return helper.formatDate(date)
    },
    async fetchLeaveStats() {
      try {
        const url = `${process.env.VUE_APP_API}/employee-leaves?per_page=1&page=1&status=pending`
        const pending = await helper.customGet(url)
        const approved = await helper.customGet(`${process.env.VUE_APP_API}/employee-leaves?per_page=1&page=1&status=approved`)
        const rejected = await helper.customGet(`${process.env.VUE_APP_API}/employee-leaves?per_page=1&page=1&status=rejected`)
        this.leaveStats = {
          pending: pending.data.count || 0,
          approved: approved.data.count || 0,
          rejected: rejected.data.count || 0
        }
      } catch (error) {
        // Non-blocking: stats fetch failure should not disrupt the page
        alert(error)
      }
    },
    async fetchEmployees() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/employees?per_page=1000`)
        this.employees = response.data.data || []
      } catch (error) {
        this.$swal({ icon: 'error', title: this.$t('error'), text: error.message })
      }
    },
    async fetchLeaves() {
      try {
        this.loading = true
        this.leaves = []

        const params = new URLSearchParams({ page: this.currentPage, per_page: this.perPage })
        this.appendSortParams(params)
        if (this.search) params.append('keyword', this.search)
        if (this.filters.leave_type) params.append('leave_type', this.filters.leave_type)
        if (this.filters.status) params.append('status', this.filters.status)
        const url = `${process.env.VUE_APP_API}/employee-leaves?${params.toString()}`

        const response = await helper.customGet(url)

        this.leaves = response.data.data || []
        this.total = response.data.count || 0

        // Build pagination array
        this.pagination = []
        let maxPagination = Math.ceil(this.total / this.perPage)
        for(let i = 1; i <= maxPagination; i++){
          this.pagination.push(i)
        }

        this.loading = false
      } catch (error) {
        this.$swal({ icon: 'error', title: this.$t('error'), text: error.message })
        this.leaves = []
        this.total = 0
        this.loading = false
      }
    },
    create() {
      this.leaveForm = {
        employee_id: '',
        leave_type: 'annual',
        start_date: '',
        end_date: '',
        reason: '',
        days_count: 0
      }
      this.selectedEmployee = {}
      this.createModal.show()
    },
    viewLeave(leave) {
      this.selectedLeave = leave
      this.viewModal.show()
    },
    async approveLeave(leave) {
      const result = await this.$swal({
        title: this.$t('approve_leave'),
        text: `${this.$t('approve_leave_for')} ${leave.days_count} ${this.$t('days')}?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('approve'),
        cancelButtonText: this.$t('cancel')
      })

      if (result.isConfirmed) {
        try {
          await helper.customPost(`${process.env.VUE_APP_API}/employee-leaves/${leave.id}/approve`, {})
          this.fetchLeaves()
          this.$toast?.open({ message: this.$t('leave_approved_successfully'), type: 'success', position: 'bottom' })
            || window.vueOpener?.open({ message: this.$t('leave_approved_successfully'), type: 'success', position: 'bottom' })
        } catch (error) {
          this.$swal({
            icon: 'error',
            title: this.$t('error'),
            text: error.message
          })
        }
      }
    },
    async rejectLeave(leave) {
      const { value: reason } = await this.$swal({
        title: this.$t('reject_leave'),
        input: 'textarea',
        inputLabel: this.$t('rejection_reason'),
        inputPlaceholder: this.$t('enter_reason'),
        showCancelButton: true,
        confirmButtonText: this.$t('reject'),
        cancelButtonText: this.$t('cancel')
      })

      if (reason) {
        try {
          await helper.customPost(`${process.env.VUE_APP_API}/employee-leaves/${leave.id}/reject`, {
            rejection_reason: reason
          })
          this.fetchLeaves()
          this.$toast?.open({ message: this.$t('leave_rejected_successfully'), type: 'success', position: 'bottom' })
            || window.vueOpener?.open({ message: this.$t('leave_rejected_successfully'), type: 'success', position: 'bottom' })
        } catch (error) {
          this.$swal({
            icon: 'error',
            title: this.$t('error'),
            text: error.message
          })
        }
      }
    },
    async deleteLeave(leave) {
      const result = await this.$swal.fire({
        title: this.$t('are_you_sure'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('delete')
      })

      if (result.isConfirmed) {
        try {
          await helper.customDelete(`${process.env.VUE_APP_API}/employee-leaves/${leave.id}`)
          this.leaves = helper.withoutDeletedRecords(this.leaves, leave.id)
          this.total = Math.max(0, this.total - 1)
        } catch (error) {
          this.$swal({ icon: 'error', title: this.$t('error'), text: error.message })
        }
      }
    },
    onEmployeeChange() {
      const employee = this.employees.find(e => e.id === this.leaveForm.employee_id)
      if (employee) {
        this.selectedEmployee = employee
      }
    },
    calculateDays() {
      if (this.leaveForm.start_date && this.leaveForm.end_date) {
        const start = new Date(this.leaveForm.start_date)
        const end = new Date(this.leaveForm.end_date)
        if (end < start) {
          this.leaveForm.days_count = 0
          return
        }
        const diffTime = end - start
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
        this.leaveForm.days_count = diffDays
      }
    },
    async saveLeave() {
      if (!this.leaveForm.employee_id || !this.leaveForm.start_date || !this.leaveForm.end_date) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: this.$t('please_fill_required_fields')
        })
        return
      }
      if (!Number(this.leaveForm.days_count)) this.calculateDays()
      if (this.leaveForm.days_count <= 0) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: 'La date de fin doit être postérieure ou égale à la date de début.'
        })
        return
      }

      try {
        const leaveData = {
          ...this.leaveForm,
          status: 'pending'
        }

        await helper.customPost(`${process.env.VUE_APP_API}/employee-leaves`, leaveData)
        this.createModal.hide()
        this.fetchLeaves()
        window.vueOpener.open({
          message: this.$t('leave_request_submitted_successfully'),
          type: 'success',
          position: 'bottom'
        })
      } catch (error) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: error.message
        })
      }
    },
    changePage(page) {
      this.currentPage = page
      this.fetchLeaves()
    }
  }
}
</script>

<style scoped>
</style>
